/**
 * Générateur — Calculer des probabilités.
 * Attendu ID : 168 | Niveau : 4ème | Domaine : Probabilités
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`) :
 *   l. 215 « Il calcule des probabilités. »
 *   l. 237-238 (type d'exercice) « Il calcule des probabilités dans des cas
 *          d'équiprobabilité comme les osselets (à partir d'informations
 *          admises sur les probabilités de chaque face), des cibles (par
 *          calcul d'aires)… »
 * Cours du prof (19Proba) : dé à six faces, jetons ; « 20 chances sur 50 »,
 * 20/50 = 2/5 = 0,4 : la fraction brute AVANT l'irréductible (jamais
 * « irréductible » exigé ici : toute écriture égale est juste). Mise en œuvre
 * 2026 p. 37 : « détermine sa probabilité comme somme des probabilités des
 * issues » (les osselets).
 *
 * Revue 2026-09-25 (vague « sept objectifs 4e fermés », DECISIONS_167-170.md) :
 * QUATRE cartes, chacune Saisie + QCM ; rappel brouillon sur tout l'attendu ;
 * calculatrice sur le seul sous-type disque de la cible.
 *   1. « Le dé ou le sac de jetons » (id `equiprobable`, l. 215 / l. 237) : dé
 *      équilibré à six faces ou sac de N jetons (6 à 12) « chaque jeton a la
 *      même probabilité d'être tiré » (l'équiprobabilité se déclare, BO
 *      l. 1160) ; événement à PLUSIEURS issues (pair, multiple de 3 sur le
 *      sac, inférieur ou égal à s, supérieur ou égal à s), 1 < favorables < N ;
 *      p = 1/2 dans un tirage sur cinq au plus (rejet) ; réponse en fraction,
 *      toute écriture égale admise (comparaison d'expression, liste finie).
 *   2. « Le damier » (id `damier`, l. 238, cible à cases égales) : L × C cases
 *      identiques (3 à 5 × 3 à 6), F coloriées en bleu et DISPERSÉES (au moins
 *      deux lignes et deux colonnes) ; F et N ne sont PAS écrits, l'élève
 *      compte sur la figure (imageUrl, alt sans les nombres) ; p = 1/2 rare.
 *   3. « La cible » (id `cible`, l. 238 « par calcul d'aires »,
 *      contenu.sousType ∈ {rectangle, disque}) : rectangle L sur l (entiers
 *      ≤ 20) à zone carrée ou rectangulaire cotée, probabilité EXACTE
 *      (fraction, ou écriture décimale quand elle existe : la consigne le
 *      dit) ; disque de rayon r dans un carré de côté S (2r ≤ S), « arrondie
 *      au centième », centième non nul, marge d'arrondi sûre (même arrondi
 *      avec 3,14 et 3,1416), calculatrice: true. UNE seule saisie : la
 *      probabilité (ni les aires, ni le pourcentage, ni le contraire, aucun
 *      « Rappel : » de méthode).
 *   4. « Les osselets » (id `osselets`, l. 237-238) : osselet, dé pipé ou roue
 *      à secteurs inégaux, QUATRE faces dont les probabilités sont DONNÉES
 *      dans un tableau (centièmes multiples de 5, somme exactement 1, deux à
 *      deux distinctes), « Quelle est la probabilité d'obtenir 1 ou 4 ? » (la
 *      somme de deux faces, jamais 0,5) ; réponse dans la forme du tableau
 *      (décimal ou pourcentage).
 * Chaque QCM : quatre options distinctes par texte ET par valeur, jamais une
 * option égale à la clé, au plus une option au-dessus de 1, jamais 0 ni 1 ;
 * familles nommées dans REGLES_DISTRACTEURS_QCM, codes dans
 * _meta.params.distracteurs ; la clé affichée telle que l'élève la compte
 * (f/N, aire sur aire), pas réduite.
 *
 * SORTI (et pourquoi) : « Une urne de boules » (automatisme 5e 1082 et redite
 * de 170) ; « Quel calcul ? » (formule nue, f et N écrits ; nom de carte
 * interdit) ; le Vrai/Faux de méthode (règles nues, univers jamais décrit, la
 * ligne « > 1 » est 169) ; les distracteurs « fraction non réduite » (égaux à
 * la clé, 32 à 59 % des QCM de juillet) ; l'exigence « fraction irréductible » ;
 * les QCM à trois options ; l'événement « obtenir le nombre x » (une seule
 * issue, 5e) ; le sac à deux critères du cours, les deux épreuves, les
 * fréquences (absents du plafond).
 * SACRIFIÉS (jamais tirés) : le dé « multiple de 3 » ({3 ; 6}, mise en œuvre
 * 2026 p. 37 : le multiple de 3 ne sort que sur le sac) ; la cible carrée de
 * 40 cm à disque de rayon 10 cm (doc prof, exclusion NOMMÉE) ; le sac de 50
 * jetons du cours (hors plage).
 * Second chemins (throw) : les favorables recomptés par une formule fermée ;
 * les cases bleues recomptées dans le SVG produit ; les dimensions de la
 * cible relues dans l'énoncé ; le tableau des faces relu dans le HTML produit.
 * Solveurs test-side : tests/solvers/probabilites-4e.js ; auto-test :
 * tests/gen168.self.test.js.
 */
import { randomChoice, randInt, shuffle, makeQcm, makeSaisie, fracTex } from '../../../utils';
import {
    RAPPEL_BROUILLON, BLEU_FAVORABLE, frCent, frDixMillieme, ecrituresFraction, ecrituresDecimales,
    elDamier, elCibleRectangle, elCibleDisque, svgDamier,
} from './probas';
import { ligneTable, tableEnonce } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 168;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_EQUI = 'Le dé ou le sac de jetons';
const L_DAMIER = 'Le damier';
const L_CIBLE = 'La cible';
const L_OSSELETS = 'Les osselets';

const strip = (html) => String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const EPS = 1e-9;

// ─── Sélection des distracteurs (commune aux quatre QCM) ─────

/**
 * Trois distracteurs parmi des candidats { code, texte, valeur } : le
 * principal d'abord s'il est admissible, complété par le premier couple
 * admissible de la rotation (mélangée) ; sans principal, le premier triplet
 * admissible. Un triplet est admissible si ses membres diffèrent de la clé et
 * entre eux par le code, le texte ET la valeur, ne valent ni 0 ni 1, et
 * comptent au plus une valeur au-dessus de 1. La recherche est exhaustive
 * (au plus C(8, 3) = 56 triplets) : une solution existante est toujours
 * trouvée ; throw sinon.
 */
const admissible = (cle, liste) => liste.every((c) => c && Number.isFinite(c.valeur) && c.valeur > 0 && Math.abs(c.valeur - 1) > EPS
    && c.texte !== cle.texte && Math.abs(c.valeur - cle.valeur) > EPS)
    && liste.every((c, i) => liste.slice(i + 1).every((d) => d.code !== c.code && d.texte !== c.texte && Math.abs(d.valeur - c.valeur) > EPS))
    && liste.filter((c) => c.valeur > 1).length <= 1;

const choisirDistracteurs = (cle, principal, rotation) => {
    const r = shuffle(rotation);
    if (principal && admissible(cle, [principal])) {
        for (let i = 0; i < r.length; i++) {
            for (let j = i + 1; j < r.length; j++) {
                if (admissible(cle, [principal, r[i], r[j]])) return [principal, r[i], r[j]];
            }
        }
    }
    for (let i = 0; i < r.length; i++) {
        for (let j = i + 1; j < r.length; j++) {
            for (let k = j + 1; k < r.length; k++) {
                if (admissible(cle, [r[i], r[j], r[k]])) return [r[i], r[j], r[k]];
            }
        }
    }
    throw new Error(`gen_168 : distracteurs insuffisants pour ${cle.texte}`);
};

const optionFrac = (code, n, d) => ({ code, texte: fracTex(n, d), valeur: n / d });

/** Le QCM assemblé, avec ses codes dans _meta.params. */
const qcmAvec = (cle, distracteurs, texte, label, params) => {
    const options = [cle, ...distracteurs];
    if (new Set(options.map((o) => o.texte)).size !== 4) throw new Error('gen_168 : options en double');
    const payload = makeQcm(options.map((o) => o.texte), cle.texte, texte, opts(label));
    payload._meta.params = { ...params, distracteurs: distracteurs.map((d) => d.code) };
    return payload;
};

/** Une saisie « en fraction, toute écriture égale » : comparaison d'expression, liste finie (simplifications de la fraction comptée, dixièmes, centièmes, millièmes). */
const saisieFraction = (num, den, texte, label, params, decimales = false) => {
    const ecritures = ecrituresFraction(num, den);
    const dec = decimales ? ecrituresDecimales(num, den) : null;
    const payload = makeSaisie(ecritures[0], texte, {
        ...opts(label), strict: false, comparaison: 'expression',
        reponses_acceptees: [...ecritures.slice(1), ...(dec || [])],
    });
    payload._meta.params = { ...params, reponse: ecritures[0] };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE 1 « Le dé ou le sac de jetons »
// ═════════════════════════════════════════════════════════════

/** Le dé du cours (six faces) ou un sac de N jetons ; le multiple de 3 ne sort jamais sur le dé (sacrifié). */
const tirerSupport = () => {
    if (randInt(0, 1) === 0) {
        return { id: 'de', N: 6, mot: 'nombre', intro: '<p>On lance un dé équilibré à six faces, numérotées de 1 à 6.</p>' };
    }
    const N = randInt(6, 12);
    return {
        id: 'sac', N, mot: 'numéro',
        intro: `<p>Un sac contient ${N} jetons numérotés de 1 à ${N}. On tire un jeton au hasard : chaque jeton a la même probabilité d'être tiré.</p>`,
    };
};

const phraseEv = (ev, S) => {
    switch (ev.code) {
        case 'pair': return `obtenir un ${S.mot} pair`;
        case 'multiple': return `obtenir un multiple de ${ev.d}`;
        case 'infeg': return `obtenir un ${S.mot} inférieur ou égal à ${ev.s}`;
        case 'supeg': return `obtenir un ${S.mot} supérieur ou égal à ${ev.s}`;
        default: throw new Error('gen_168 : événement inconnu');
    }
};

/** Les favorables comptés issue par issue (chemin 1). */
const compterFavorables = (ev, N) => {
    let f = 0;
    for (let k = 1; k <= N; k++) {
        if ((ev.code === 'pair' && k % 2 === 0) || (ev.code === 'multiple' && k % ev.d === 0)
            || (ev.code === 'infeg' && k <= ev.s) || (ev.code === 'supeg' && k >= ev.s)) f += 1;
    }
    return f;
};

/** Les favorables par formule fermée (chemin 2) : ⌊N/2⌋, ⌊N/3⌋, s, N − s + 1. */
const favorablesFormule = (ev, N) => {
    if (ev.code === 'pair') return Math.floor(N / 2);
    if (ev.code === 'multiple') return Math.floor(N / ev.d);
    if (ev.code === 'infeg') return ev.s;
    return N - ev.s + 1;
};

/** Un événement à plusieurs issues, 2 ≤ favorables ≤ N − 2 ; p = 1/2 accepté un tirage sur cinq. */
const tirerEvenement = (S) => {
    const N = S.N;
    for (let essai = 0; essai < 40; essai++) {
        const pool = [
            { code: 'pair' },
            ...(S.id === 'sac' ? [{ code: 'multiple', d: 3 }] : []),
            { code: 'infeg', s: randInt(2, N - 2) },
            { code: 'supeg', s: randInt(3, N - 1) },
        ];
        const ev = randomChoice(pool);
        const f = compterFavorables(ev, N);
        if (f < 2 || f > N - 2) continue;
        if (2 * f === N && randInt(1, 5) !== 1) continue;
        return ev;
    }
    return { code: 'infeg', s: 2 };   // repli déterministe vérifié : 2 favorables
};

const situationEqui = (S, ev) => ({
    f: compterFavorables(ev, S.N),
    phrase: phraseEv(ev, S),
});

/** Second chemin : formule fermée et bornes. */
const verifierEqui = (S, ev, f) => {
    if (favorablesFormule(ev, S.N) !== f || f < 2 || f > S.N - 2) throw new Error(`gen_168 : favorables ${f} pour « ${phraseEv(ev, S)} » (N = ${S.N})`);
};

const genEquiSaisie = () => {
    const S = tirerSupport();
    const ev = tirerEvenement(S);
    const { f, phrase } = situationEqui(S, ev);
    verifierEqui(S, ev, f);
    const texte = `${S.intro}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité d'${phrase} ? Donne-la en fraction.</b></p>`;
    return saisieFraction(f, S.N, texte, L_EQUI, { support: S.id, N: S.N, evenement: phrase, f });
};

/** Familles communes aux univers comptés (jetons, cases) : f favorables sur N. */
const candidatsComptage = (f, N) => ({
    principal: optionFrac('favorables_sur_defavorables', f, N - f),
    rotation: [
        optionFrac('un_sur_favorables', 1, f),
        optionFrac('contraire', N - f, N),
        optionFrac('comptage', f + 1, N),
        optionFrac('comptage', f - 1, N),
        optionFrac('inverse', N, f),
    ],
});

const genEquiQcm = () => {
    const S = tirerSupport();
    const ev = tirerEvenement(S);
    const { f, phrase } = situationEqui(S, ev);
    verifierEqui(S, ev, f);
    const cle = optionFrac(null, f, S.N);
    const { principal, rotation } = candidatsComptage(f, S.N);
    const texte = `${S.intro}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité d'${phrase} ?</b></p>`;
    return qcmAvec(cle, choisirDistracteurs(cle, principal, rotation), texte, L_EQUI, { support: S.id, N: S.N, evenement: phrase, f, reponse: `${f}/${S.N}` });
};

// ═════════════════════════════════════════════════════════════
// CARTE 2 « Le damier »
// ═════════════════════════════════════════════════════════════

/** Les index de cases utilisent-ils au moins deux lignes et deux colonnes ? */
const disperse = (fav, C) => new Set(fav.map((i) => Math.floor(i / C))).size >= 2 && new Set(fav.map((i) => i % C)).size >= 2;

/** L × C cases (3 à 5 × 3 à 6), F bleues dispersées, 2 ≤ F ≤ N − 2 ; p = 1/2 accepté un tirage sur cinq. */
export const tirerDamier = () => {
    const L = randInt(3, 5), C = randInt(3, 6);
    const N = L * C;
    let F = randInt(2, N - 2);
    for (let essai = 0; essai < 40 && 2 * F === N && randInt(1, 5) !== 1; essai++) F = randInt(2, N - 2);
    const tous = [...Array(N).keys()];
    let fav = null;
    for (let essai = 0; essai < 20 && !fav; essai++) {
        const cand = shuffle(tous).slice(0, F);
        if (disperse(cand, C)) fav = cand;
    }
    // Repli déterministe : les deux coins opposés (lignes et colonnes différentes) puis le reste au hasard.
    if (!fav) fav = [0, N - 1, ...shuffle(tous.slice(1, N - 1)).slice(0, F - 2)];
    fav.sort((a, b) => a - b);
    return { L, C, N, F, fav };
};

const ALT_DAMIER = 'Damier de cases identiques, en lignes et en colonnes ; certaines cases sont coloriées en bleu, les autres sont blanches.';

/** Second chemin : les cases bleues et le total RECOMPTÉS dans le SVG produit. */
const verifierDamier = (d) => {
    const svg = svgDamier(d.L, d.C, d.fav);
    const rects = (svg.match(/<rect /g) || []).length;
    const bleues = (svg.match(new RegExp(`fill="${BLEU_FAVORABLE}"`, 'g')) || []).length;
    if (rects !== d.N || bleues !== d.F || !disperse(d.fav, d.C) || d.F < 2 || d.F > d.N - 2) {
        throw new Error(`gen_168 : damier ${d.L}×${d.C}, ${bleues}/${rects} relues pour ${d.F}/${d.N}`);
    }
};

const SITUATION_DAMIER = '<p>Une fléchette est lancée au hasard sur le damier ci-dessous : elle atteint toujours le damier, et toutes les cases sont identiques.</p>';

const genDamierSaisie = () => {
    const d = tirerDamier();
    verifierDamier(d);
    const texte = `${SITUATION_DAMIER}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne une case bleue ? Donne-la en fraction.</b></p>`;
    const payload = saisieFraction(d.F, d.N, texte, L_DAMIER, { L: d.L, C: d.C, N: d.N, F: d.F, fav: d.fav });
    payload.elements_additionnels = elDamier(d.L, d.C, d.fav, ALT_DAMIER);
    return payload;
};

const genDamierQcm = () => {
    const d = tirerDamier();
    verifierDamier(d);
    const cle = optionFrac(null, d.F, d.N);
    const { principal, rotation } = candidatsComptage(d.F, d.N);
    rotation.push(optionFrac('lignes_pour_cases', d.F, d.L), optionFrac('lignes_pour_cases', d.F, d.C));
    const texte = `${SITUATION_DAMIER}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne une case bleue ?</b></p>`;
    const payload = qcmAvec(cle, choisirDistracteurs(cle, principal, rotation), texte, L_DAMIER, { L: d.L, C: d.C, N: d.N, F: d.F, fav: d.fav, reponse: `${d.F}/${d.N}` });
    payload.elements_additionnels = elDamier(d.L, d.C, d.fav, ALT_DAMIER);
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE 3 « La cible » (sousType rectangle | disque)
// ═════════════════════════════════════════════════════════════

const FLECHETTE = 'Une fléchette est lancée au hasard : elle atteint toujours la cible, et chaque point de la cible a la même probabilité d\'être touché.';

/** Cible L sur l (L de 8 à 20, l de 5 à L − 1), zone carrée ou rectangulaire laissant au moins 2 cm de chaque côté (la cote de la zone tient dans la cible). */
export const tirerRectangle = () => {
    const L = randInt(8, 20);
    const l = randInt(5, L - 1);
    const carree = randInt(0, 1) === 0;
    let a; let b;
    if (carree) { a = randInt(2, l - 2); b = a; } else {
        a = randInt(2, L - 2);
        b = randInt(2, l - 2);
        if (a === b) a = a + 1 <= L - 2 ? a + 1 : a - 1;
    }
    return { L, l, a, b, carree: a === b };
};

const descRectangle = (c) => `<p>Une cible rectangulaire mesure ${c.L} cm sur ${c.l} cm. Elle contient une zone ${c.carree ? `carrée de ${c.a} cm de côté` : `rectangulaire de ${c.a} cm sur ${c.b} cm`}, coloriée en bleu. ${FLECHETTE}</p>`;
const altRectangle = (c) => `Cible rectangulaire de ${c.L} cm sur ${c.l} cm, avec une zone bleue ${c.carree ? `carrée de ${c.a} cm de côté` : `rectangulaire de ${c.a} cm sur ${c.b} cm`} au milieu.`;

/** Second chemin : les dimensions RELUES dans l'énoncé rendu, aires recalculées. */
const relireRectangle = (texte) => {
    const t = strip(texte);
    const m = t.match(/mesure (\d+) cm sur (\d+) cm\. Elle contient une zone (?:carrée de (\d+) cm de côté|rectangulaire de (\d+) cm sur (\d+) cm)/);
    if (!m) throw new Error('gen_168 : cible rectangulaire illisible');
    const L = Number(m[1]), l = Number(m[2]);
    const a = Number(m[3] ?? m[4]), b = m[3] ? Number(m[3]) : Number(m[5]);
    if (!(a <= L - 2 && b <= l - 2 && a >= 2 && b >= 2)) throw new Error(`gen_168 : zone ${a}×${b} dans ${L}×${l}`);
    return { aireZone: a * b, aireCible: L * l, a, b, L, l };
};

/**
 * Les couples (côté S, rayon r) du disque : 2r ≤ S, hors (40, 10) (sacrifié,
 * doc prof), centième de la probabilité NON nul, marge d'arrondi sûre (la
 * partie décimale de 100p à plus de 0,05 d'un demi, et le même arrondi avec
 * π = 3,14 et 3,1416). Liste figée au chargement.
 */
const S_POOL = [20, 24, 30, 36, 40, 48, 50, 60];
const R_POOL = [3, 4, 5, 6, 8, 10, 12, 15, 18, 20];
const centiemesDisque = (S, r, pi = Math.PI) => Math.round((100 * pi * r * r) / (S * S));
export const COUPLES_DISQUE = (() => {
    const out = [];
    S_POOL.forEach((S) => R_POOL.forEach((r) => {
        if (2 * r > S || (S === 40 && r === 10)) return;
        const x = (100 * Math.PI * r * r) / (S * S);
        const p100 = Math.round(x);
        const marge = Math.abs((x - Math.floor(x)) - 0.5);
        if (p100 % 10 === 0 || marge < 0.05) return;
        if (centiemesDisque(S, r, 3.14) !== p100 || centiemesDisque(S, r, 3.1416) !== p100) return;
        out.push({ S, r, p100 });
    }));
    return out;
})();

const descDisque = (c) => `<p>Une cible carrée mesure ${c.S} cm de côté. Elle contient un disque de rayon ${c.r} cm, colorié en bleu. ${FLECHETTE}</p>`;
const altDisque = (c) => `Cible carrée de ${c.S} cm de côté, avec un disque bleu de rayon ${c.r} cm au centre.`;

/** Second chemin : côté et rayon RELUS dans l'énoncé rendu, arrondi recalculé. */
const relireDisque = (texte) => {
    const m = strip(texte).match(/mesure (\d+) cm de côté\. Elle contient un disque de rayon (\d+) cm/);
    if (!m) throw new Error('gen_168 : cible carrée illisible');
    return centiemesDisque(Number(m[1]), Number(m[2]));
};

/** Sous-type, rappel, calculatrice sur le disque seul. */
const habillerCible = (payload, sousType) => {
    payload.contenu.sousType = sousType;
    if (sousType === 'disque') payload.contenu.calculatrice = true;
    return payload;
};

const genCibleSaisie = () => {
    if (randInt(0, 1) === 0) {
        const c = tirerRectangle();
        const dec = ecrituresDecimales(c.a * c.b, c.L * c.l);
        const consigne = dec ? 'Donne-la en fraction ou en écriture décimale.' : 'Donne-la en fraction.';
        const texte = `${descRectangle(c)}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne la zone bleue ? ${consigne}</b></p>`;
        const lu = relireRectangle(texte);
        if (lu.aireZone !== c.a * c.b || lu.aireCible !== c.L * c.l) throw new Error('gen_168 : aires relues');
        const payload = saisieFraction(c.a * c.b, c.L * c.l, texte, L_CIBLE, { sousType: 'rectangle', ...c, decimale: dec ? dec[0] : null }, true);
        payload.elements_additionnels = elCibleRectangle(c.L, c.l, c.a, c.b, altRectangle(c));
        return habillerCible(payload, 'rectangle');
    }
    const c = randomChoice(COUPLES_DISQUE);
    const texte = `${descDisque(c)}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne le disque bleu ? Donne la valeur arrondie au centième.</b></p>`;
    if (relireDisque(texte) !== c.p100 || c.p100 % 10 === 0) throw new Error('gen_168 : centièmes relus');
    const payload = makeSaisie(frCent(c.p100), texte, { ...opts(L_CIBLE), strict: false });
    payload._meta.params = { sousType: 'disque', S: c.S, r: c.r, reponse: c.p100 };
    payload.elements_additionnels = elCibleDisque(c.S, c.r, altDisque(c));
    return habillerCible(payload, 'disque');
};

const genCibleQcm = () => {
    if (randInt(0, 1) === 0) {
        const c = tirerRectangle();
        const aireZone = c.a * c.b, aireCible = c.L * c.l;
        const cle = optionFrac(null, aireZone, aireCible);
        const principal = randInt(0, 1) === 0 ? optionFrac('longueur_pour_aire', c.a, c.L) : optionFrac('longueur_pour_aire', c.b, c.l);
        const rotation = [
            optionFrac('contraire', aireCible - aireZone, aireCible),
            optionFrac('perimetre_pour_aire', 2 * (c.a + c.b), 2 * (c.L + c.l)),
            optionFrac('inverse', aireCible, aireZone),
        ];
        const texte = `${descRectangle(c)}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne la zone bleue ?</b></p>`;
        const lu = relireRectangle(texte);
        if (lu.aireZone !== aireZone || lu.aireCible !== aireCible) throw new Error('gen_168 : aires relues');
        const payload = qcmAvec(cle, choisirDistracteurs(cle, principal, rotation), texte, L_CIBLE, { sousType: 'rectangle', ...c, reponse: `${aireZone}/${aireCible}` });
        payload.elements_additionnels = elCibleRectangle(c.L, c.l, c.a, c.b, altRectangle(c));
        return habillerCible(payload, 'rectangle');
    }
    const c = randomChoice(COUPLES_DISQUE);
    const optionCent = (code, centiemes) => ({ code, texte: frCent(centiemes), valeur: centiemes / 100 });
    const cle = optionCent(null, c.p100);
    const rotation = [
        optionCent('diametre_pour_rayon', centiemesDisque(c.S, 2 * c.r)),
        optionCent('rayon_sans_carre', Math.round((100 * Math.PI * c.r) / (c.S * c.S))),
        optionCent('inverse', Math.round((100 * c.S * c.S) / (Math.PI * c.r * c.r))),
        optionCent('contraire', 100 - c.p100),
    ];
    const texte = `${descDisque(c)}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité que la fléchette atteigne le disque bleu, arrondie au centième ?</b></p>`;
    if (relireDisque(texte) !== c.p100) throw new Error('gen_168 : centièmes relus');
    const payload = qcmAvec(cle, choisirDistracteurs(cle, null, rotation), texte, L_CIBLE, { sousType: 'disque', S: c.S, r: c.r, reponse: c.p100 });
    payload.elements_additionnels = elCibleDisque(c.S, c.r, altDisque(c));
    return habillerCible(payload, 'disque');
};

// ═════════════════════════════════════════════════════════════
// CARTE 4 « Les osselets »
// ═════════════════════════════════════════════════════════════

const SUPPORTS_OSSELETS = [
    { id: 'osselet', intro: '<p>On lance un osselet à quatre faces, numérotées de 1 à 4. La probabilité d\'obtenir chaque face est donnée dans le tableau.</p>', entete: 'Face', question: (i, j) => `Quelle est la probabilité d'obtenir ${i} ou ${j} ?` },
    { id: 'de_pipe', intro: '<p>On lance un dé pipé à quatre faces, numérotées de 1 à 4. La probabilité d\'obtenir chaque face est donnée dans le tableau.</p>', entete: 'Face', question: (i, j) => `Quelle est la probabilité d'obtenir ${i} ou ${j} ?` },
    { id: 'roue', intro: '<p>Une roue de loterie a quatre secteurs de tailles différentes, numérotés de 1 à 4. La probabilité que la roue s\'arrête sur chaque secteur est donnée dans le tableau.</p>', entete: 'Secteur', question: (i, j) => `Quelle est la probabilité que la roue s'arrête sur le secteur ${i} ou sur le secteur ${j} ?` },
];

/** Quatre centièmes distincts, multiples de 5, de 5 à 60, de somme 100 ; deux faces demandées de somme différente de 50. */
export const tirerOsselet = () => {
    const pool = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
    for (let essai = 0; essai < 100; essai++) {
        const trois = shuffle(pool).slice(0, 3);
        const quatre = 100 - trois[0] - trois[1] - trois[2];
        if (quatre < 5 || quatre > 60 || trois.includes(quatre)) continue;
        const faces = shuffle([...trois, quatre]);
        const [i, j] = shuffle([0, 1, 2, 3]).slice(0, 2).sort((x, y) => x - y);
        if (faces[i] + faces[j] === 50) continue;
        return { faces, i, j };
    }
    return { faces: [10, 40, 15, 35], i: 0, j: 2 };   // repli déterministe vérifié : 10 + 15 = 25
};

const ecritOsselet = (forme, centiemes) => (forme === 'decimal' ? frCent(centiemes) : `${centiemes} %`);

const tableauOsselet = (S, forme, faces) => tableEnonce(
    ligneTable(S.entete, ['1', '2', '3', '4']),
    ligneTable('Probabilité', faces.map((c) => ecritOsselet(forme, c))),
);

/** Second chemin : les quatre cases RELUES dans le HTML produit, somme 1, somme des deux faces demandées. */
const relireOsselet = (texte, forme) => {
    const t = String(texte);
    const cases = [...t.matchAll(/<td[^>]*>([^<]*)<\/td>/g)].map((m) => m[1].trim());
    if (cases.length !== 8) throw new Error('gen_168 : tableau des faces illisible');
    const valeurs = cases.slice(4).map((s) => {
        const m = s.match(forme === 'decimal' ? /^(\d+)(?:,(\d{1,2}))?$/ : /^(\d+) %$/);
        if (!m) throw new Error(`gen_168 : case « ${s} »`);
        return forme === 'decimal' ? Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0')) : Number(m[1]);
    });
    const q = strip(t).match(/obtenir (\d) ou (\d) \?|secteur (\d) ou sur le secteur (\d) \?/);
    if (!q) throw new Error('gen_168 : faces demandées illisibles');
    const i = Number(q[1] ?? q[3]) - 1, j = Number(q[2] ?? q[4]) - 1;
    if (valeurs.reduce((s, v) => s + v, 0) !== 100 || new Set(valeurs).size !== 4) throw new Error('gen_168 : tableau des faces incohérent');
    return valeurs[i] + valeurs[j];
};

const situationOsselet = () => {
    const S = randomChoice(SUPPORTS_OSSELETS);
    const forme = randomChoice(['decimal', 'pourcentage']);
    const o = tirerOsselet();
    const somme = o.faces[o.i] + o.faces[o.j];
    const consigne = forme === 'decimal' ? 'Donne-la en écriture décimale.' : 'Donne-la en pourcentage.';
    return { S, forme, o, somme, consigne, tableau: tableauOsselet(S, forme, o.faces) };
};

const genOsseletsSaisie = () => {
    const { S, forme, o, somme, consigne, tableau } = situationOsselet();
    const texte = `${S.intro}${tableau}${RAPPEL_BROUILLON}<p><b>${S.question(o.i + 1, o.j + 1)} ${consigne}</b></p>`;
    if (relireOsselet(texte, forme) !== somme || somme === 50) throw new Error('gen_168 : somme relue');
    const payload = makeSaisie(forme === 'decimal' ? frCent(somme) : String(somme), texte, { ...opts(L_OSSELETS), strict: false, suffixe: forme === 'pourcentage' ? '%' : '' });
    payload._meta.params = { support: S.id, forme, faces: o.faces, demandees: [o.i + 1, o.j + 1], reponse: somme };
    return payload;
};

const genOsseletsQcm = () => {
    const { S, forme, o, somme, tableau } = situationOsselet();
    const c1 = o.faces[o.i], c2 = o.faces[o.j];
    const option = (code, centiemes) => ({ code, texte: ecritOsselet(forme, centiemes), valeur: centiemes / 100 });
    const cle = option(null, somme);
    const produit = forme === 'decimal'
        ? { code: 'produit', texte: frDixMillieme(c1 * c2), valeur: (c1 * c2) / 10000 }
        : { code: 'produit', texte: `${(c1 * c2) % 100 === 0 ? (c1 * c2) / 100 : frCent(c1 * c2)} %`, valeur: (c1 * c2) / 10000 };
    const rotation = [produit, option('une_seule_face', Math.max(c1, c2)), option('equiprobable_impose', 50), option('contraire', 100 - somme)];
    const texte = `${S.intro}${tableau}${RAPPEL_BROUILLON}<p><b>${S.question(o.i + 1, o.j + 1)}</b></p>`;
    if (relireOsselet(texte, forme) !== somme || somme === 50) throw new Error('gen_168 : somme relue');
    return qcmAvec(cle, choisirDistracteurs(cle, null, rotation), texte, L_OSSELETS, { support: S.id, forme, faces: o.faces, demandees: [o.i + 1, o.j + 1], reponse: somme });
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre options par construction : la clé, un principal quand la carte en a un, puis une rotation ; distinctes par le texte ET par la valeur, jamais égales à la clé (les « fractions non réduites » de juillet, égales à la clé dans 32 à 59 % des tirages, sont sorties)',
        'au plus UNE option au-dessus de 1 (l\'attendu 169 la rend repérable) ; jamais 0 ni 1',
        'la clé est affichée telle que l\'élève la compte (f/N, aire sur aire, centièmes), pas réduite : aucune simplification exigée (cours : 20/50 avant 2/5)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    equiprobable_qcm_unique: {
        support: 'dé équilibré à six faces ou sac de 6 à 12 jetons numérotés ; événement à plusieurs issues (pair, multiple de 3 sur le sac, inférieur ou égal à s, supérieur ou égal à s), 2 ≤ f ≤ N − 2',
        principal: { favorables_sur_defavorables: 'f/(N − f) : les favorables rapportés aux défavorables (fiche 5e 1083 « favorables sur perdants », transfert)' },
        rotation: {
            un_sur_favorables: '1/f : « 3 cas donc une chance sur 3 » (fiche 5e 1083)',
            contraire: '(N − f)/N : la probabilité de l\'événement contraire (BO l. 1240)',
            comptage: '(f ± 1)/N : une issue comptée en trop ou en moins',
            inverse: 'N/f : l\'écriture inversée (DEPP 4e 2023 q.15), la seule option au-dessus de 1 quand elle sort',
        },
    },
    damier_qcm_unique: {
        support: 'damier de L × C cases identiques (3 à 5 × 3 à 6), F cases bleues dispersées, F et N lus sur la figure',
        principal: 'favorables_sur_defavorables (comme la carte 1)',
        rotation: 'un_sur_favorables, contraire, comptage, inverse, plus lignes_pour_cases : F/L ou F/C, le nombre de lignes ou de colonnes pris pour le total',
    },
    cible_qcm_unique: {
        rectangle: {
            support: 'cible L sur l (entiers ≤ 20), zone carrée ou rectangulaire a sur b strictement plus petite dans les deux sens',
            principal: { longueur_pour_aire: 'a/L ou b/l : le rapport des côtés au lieu du rapport des aires (hypothèse d\'auteur)' },
            rotation: { contraire: '(Ll − ab)/(Ll)', perimetre_pour_aire: '2(a + b)/2(L + l) : le rapport des périmètres', inverse: 'Ll/ab (au-dessus de 1)' },
        },
        disque: {
            support: 'carré de côté S, disque de rayon r (COUPLES_DISQUE : 2r ≤ S, hors 40/10, centième non nul, marge d\'arrondi sûre) ; options en centièmes',
            rotation: {
                diametre_pour_rayon: 'π × (2r)² / S² : le diamètre pris pour le rayon (au-dessus de 1 quand r > 0,28 S : il compte alors pour la seule option au-dessus de 1)',
                rayon_sans_carre: 'π × r / S² : le rayon non élevé au carré (retiré s\'il s\'arrondit à 0)',
                inverse: 'S² / (π r²) : l\'aire de la cible sur celle du disque (au-dessus de 1)',
                contraire: '1 − p : la probabilité de manquer le disque',
            },
        },
    },
    osselets_qcm_unique: {
        support: 'osselet, dé pipé ou roue à quatre secteurs inégaux ; quatre probabilités données (centièmes multiples de 5, somme 1, distinctes) ; deux faces demandées, somme ≠ 0,5',
        rotation: {
            produit: 'p1 × p2 au lieu de p1 + p2 (0,2 × 0,3 = 0,06 ; 20 % × 30 % = 6 %) : hypothèse d\'auteur',
            une_seule_face: 'la plus grande des deux probabilités, l\'autre oubliée',
            equiprobable_impose: '2/4 = 0,5 ou 50 % : « deux faces sur quatre », l\'équiprobabilité imposée à un osselet',
            contraire: '1 − (p1 + p2)',
        },
        garantie: 'somme des quatre faces = 1 et somme des deux faces relues dans le tableau HTML produit (throw sinon)',
    },
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    equiprobable_texte: genEquiSaisie,
    equiprobable_qcm_unique: genEquiQcm,
    damier_texte: genDamierSaisie,
    damier_qcm_unique: genDamierQcm,
    cible_texte: genCibleSaisie,
    cible_qcm_unique: genCibleQcm,
    osselets_texte: genOsseletsSaisie,
    osselets_qcm_unique: genOsseletsQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'equiprobable', label: L_EQUI, formatsList: buildFormats('equiprobable') },
    { id: 'damier', label: L_DAMIER, formatsList: buildFormats('damier') },
    { id: 'cible', label: L_CIBLE, formatsList: buildFormats('cible') },
    { id: 'osselets', label: L_OSSELETS, formatsList: buildFormats('osselets') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

/** Fonctions internes exposées à l'auto-test (énumérations exhaustives). */
export const _interne = { choisirDistracteurs, admissible, candidatsComptage, compterFavorables, favorablesFormule, centiemesDisque, optionFrac };
